import { NavigationEventData, NavigationBatchPayload } from '../../types/navigation-tracking';
import { assessmentApi } from '../../api/openapi-axios-client';

const QUEUE_KEY = 'futuregen_nav_queue';
const BATCH_SIZE = 10;
const BATCH_INTERVAL_MS = 5000;

export class NavigationBatcher {
  private queue: NavigationEventData[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private sending = false;

  constructor() {
    this.loadQueue();
    this.startTimer();
    window.addEventListener('beforeunload', this.handleUnload);
  }

  add(event: NavigationEventData): void {
    this.queue.push(event);
    this.persistQueue();

    if (this.queue.length >= BATCH_SIZE) {
      this.flush();
    }
  }

  async flush(): Promise<void> {
    if (this.sending || this.queue.length === 0) return;

    this.sending = true;
    const batch = this.queue.splice(0, BATCH_SIZE);
    this.persistQueue();

    const payload: NavigationBatchPayload = {
      events: batch,
      batchId: `batch_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
      batchTimestamp: new Date().toISOString(),
    };

    try {
      await assessmentApi.analytics.recordNavigationBatch(payload);
    } catch (error) {
      // Put events back at the front of the queue for retry
      this.queue.unshift(...batch);
      this.persistQueue();
      console.warn('[NavigationBatcher] Failed to send batch, events re-queued', error);
    } finally {
      this.sending = false;
    }

    // If there are still events in the queue, flush again
    if (this.queue.length >= BATCH_SIZE) {
      this.flush();
    }
  }

  destroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    window.removeEventListener('beforeunload', this.handleUnload);
    this.flush();
  }

  private startTimer(): void {
    this.timer = setInterval(() => this.flush(), BATCH_INTERVAL_MS);
  }

  private handleUnload = (): void => {
    if (this.queue.length === 0) return;

    const payload: NavigationBatchPayload = {
      events: this.queue,
      batchId: `batch_${Date.now()}_beacon`,
      batchTimestamp: new Date().toISOString(),
    };

    // sendBeacon for reliable delivery on page close
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    navigator.sendBeacon('/api/assessment/analytics/navigation/batch', blob);

    this.queue = [];
    this.persistQueue();
  };

  private loadQueue(): void {
    try {
      const stored = localStorage.getItem(QUEUE_KEY);
      if (stored) {
        this.queue = JSON.parse(stored);
      }
    } catch {
      this.queue = [];
    }
  }

  private persistQueue(): void {
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(this.queue));
    } catch {
      // localStorage full or unavailable
    }
  }
}
